<template>
  <section class="tb-card" data-test="ti-trend-card">
    <header class="tb-head">
      <span class="tb-avatar" aria-hidden="true">{{ initialsOf }}</span>
      <div class="tb-who">
        <h4 class="tb-name">{{ name }}</h4>
        <p class="tb-total"><b>{{ total }}</b> follow-ups</p>
      </div>
      <button
        type="button"
        class="tb-ghost"
        data-test="ti-trend-table-toggle"
        :aria-expanded="showTable ? 'true' : 'false'"
        aria-label="Show or hide the data table"
        @click="showTable = !showTable"
      >
        <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true"><circle cx="5" cy="12" r="1.9" fill="currentColor" /><circle cx="12" cy="12" r="1.9" fill="currentColor" /><circle cx="19" cy="12" r="1.9" fill="currentColor" /></svg>
      </button>
    </header>

    <StatTiles v-if="stats.length" class="tb-stats" compact :stats="stats" :label="`${name}: totals`" />

    <ul class="tb-legend" aria-label="Chart key">
      <li><i class="tb-key tb-key--calls" aria-hidden="true"></i>Follow-ups</li>
      <li v-if="model.hasSecond"><i class="tb-key tb-key--second" aria-hidden="true"></i>{{ model.secondLabel }}</li>
    </ul>

    <svg class="tb-svg" :viewBox="`0 0 ${W} ${H}`" role="img" :aria-label="chartLabel" preserveAspectRatio="xMidYMid meet">
      <defs>
        <linearGradient :id="`${uid}-g1`" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stop-color="#3b9bff" />
          <stop offset="1" stop-color="#0a7cf5" />
        </linearGradient>
        <linearGradient :id="`${uid}-g2`" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stop-color="#bcdcfd" />
          <stop offset="1" stop-color="#8fc2fa" />
        </linearGradient>
      </defs>
      <g v-for="t in ticks" :key="`t${t.v}`">
        <line :x1="M.l" :x2="W - M.r" :y1="t.y" :y2="t.y" class="tb-grid" :class="{ 'is-axis': t.v === 0 }" :stroke-dasharray="t.v === 0 ? null : '4 5'" />
        <text :x="M.l - 8" :y="t.y + 4" text-anchor="end" class="tb-ytick">{{ t.v }}</text>
      </g>
      <g v-for="(b, i) in bars" :key="`b${i}`" class="tb-group">
        <path v-if="b.calls.h > 0" :d="b.calls.d" :fill="`url(#${uid}-g1)`"><title>{{ b.title }}</title></path>
        <path v-if="model.hasSecond && b.second.h > 0" :d="b.second.d" :fill="`url(#${uid}-g2)`"><title>{{ b.title }}</title></path>
        <text v-if="showValues && b.calls.h > 0" :x="b.calls.cx" :y="b.calls.y - 6" text-anchor="middle" class="tb-val tb-val--calls">{{ b.calls.v }}</text>
        <text v-if="showValues && model.hasSecond && b.second.h > 0" :x="b.second.cx" :y="b.second.y - 6" text-anchor="middle" class="tb-val tb-val--second">{{ b.second.v }}</text>
        <text v-if="b.showLabel" :x="b.cx" :y="H - M.b + 20" text-anchor="middle" class="tb-xlab">{{ b.label }}</text>
        <rect :x="b.bandX" :y="M.t" :width="b.bandW" :height="H - M.t - M.b + 24" class="tb-hit"><title>{{ b.title }}</title></rect>
      </g>
    </svg>

    <p class="tb-insight" data-test="ti-trend-insight"><svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true"><path d="M3 17l6-6 4 4 7-8" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" /></svg><span>{{ model.insight }}</span></p>

    <div v-if="showTable" class="tb-table" data-test="ti-trend-table">
      <table>
        <thead>
          <tr><th scope="col">Period</th><th scope="col">Follow-ups</th><th v-if="model.hasSecond" scope="col">{{ model.secondLabel }}</th></tr>
        </thead>
        <tbody>
          <tr v-for="b in model.buckets" :key="b.label"><th scope="row">{{ b.label }}</th><td>{{ b.calls }}</td><td v-if="model.hasSecond">{{ b.second }}</td></tr>
        </tbody>
      </table>
    </div>
  </section>
</template>

<script>
import { buildTrendModel, niceAxis, initials } from './outcomeModel.js';
import StatTiles from './StatTiles.vue';

const W = 520;
const H = 230;
const M = { l: 34, r: 8, t: 26, b: 34 };

let counter = 0;

// A bar with a rounded top, anchored on the baseline.
function topRound(x, y, w, h, r) {
  const rr = Math.min(r, w / 2, h);
  return `M${x},${y + h}V${y + rr}Q${x},${y} ${x + rr},${y}H${x + w - rr}Q${x + w},${y} ${x + w},${y + rr}V${y + h}Z`;
}

/**
 * One person's follow-ups over the report's periods (Month-to-Date / Custom Reports): grouped bars — follow-ups beside what came of
 * them (scheduled appointments, or claims received) — with the numbers on the bars and a one-line read-out. It is handed the SAME
 * chartData the classic bar chart draws from and shows only the numbers in it (outcomeModel.js buildTrendModel).
 */
export default {
  name: 'TrendBarsCard',
  components: { StatTiles },
  props: {
    name: { type: String, default: '' },
    total: { type: [Number, String], default: 0 },
    chartData: { type: Object, default: null },
    timeRange: { type: String, default: 'month' },
    // [{ id, label, value, sub }] — this person's four numbers (chipRows.js buildMemberStats)
    stats: { type: Array, default: () => [] },
  },
  data() {
    counter += 1;
    return { W, H, M, uid: `tb${counter}`, showTable: false };
  },
  computed: {
    model() {
      return buildTrendModel(this.chartData, this.timeRange);
    },
    initialsOf() {
      return initials(this.name);
    },
    axis() {
      return niceAxis(this.model.maxValue);
    },
    top() {
      return this.axis.top;
    },
    plotH() {
      return H - M.t - M.b;
    },
    yOf() {
      return v => M.t + this.plotH - (v / this.top) * this.plotH;
    },
    ticks() {
      const out = [];
      for (let v = 0; v <= this.top; v += this.axis.step) out.push({ v, y: this.yOf(v) });
      return out;
    },
    showValues() {
      return this.model.buckets.length <= 12;
    },
    bars() {
      const n = this.model.buckets.length;
      if (n === 0) return [];
      const band = (W - M.l - M.r) / n;
      const series = this.model.hasSecond ? 2 : 1;
      const gap = series === 2 ? Math.min(8, band * 0.08) : 0;
      const bw = Math.max(4, Math.min(40, (band * 0.62 - gap) / series));
      const radius = Math.min(10, bw / 2);
      const base = this.yOf(0);
      const every = Math.max(1, Math.ceil(26 / band)); // label every k-th period when they would collide
      return this.model.buckets.map((b, i) => {
        const cx = M.l + band * i + band / 2;
        const x1 = series === 2 ? cx - gap / 2 - bw : cx - bw / 2;
        const x2 = cx + gap / 2;
        const bar = (v, x) => {
          const y = this.yOf(v);
          return { v, x, y, h: base - y, cx: x + bw / 2, d: topRound(x, y, bw, base - y, radius) };
        };
        return {
          label: b.label,
          cx,
          bandX: M.l + band * i,
          bandW: band,
          showLabel: i % every === 0,
          calls: bar(b.calls, x1),
          second: bar(b.second, x2),
          title: `${b.label}: ${b.calls} follow-ups${this.model.hasSecond ? `, ${b.second} ${this.model.secondLabel.toLowerCase()}` : ''}`,
        };
      });
    },
    chartLabel() {
      return `${this.name}: follow-ups by ${this.timeRange === 'month' ? 'month' : this.timeRange === 'week' ? 'week' : 'day'}. ${this.model.insight} A data table is available.`;
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../../../components/Followups/mixins';

.tb-card {
  @include fu-font-base;
  background: $fu-surface;
  border: 1px solid $fu-border;
  border-radius: 24px;
  padding: 18px 20px;
  box-shadow: $fu-shadow-sm;
  min-width: 0;
}

.tb-head {
  display: flex;
  align-items: center;
  gap: 12px;
}

.tb-avatar {
  flex: none;
  width: 38px;
  height: 38px;
  border-radius: 50%;
  display: grid;
  place-items: center;
  font-size: 12px;
  font-weight: $fu-fw-semibold;
  color: $fu-text-strong;
  background: $fu-surface-sunken;
  border: 1px solid $fu-border;
}

.tb-who { flex: 1; min-width: 0; }

.tb-name {
  margin: 0;
  font-size: 15px;
  font-weight: $fu-fw-semibold;
  color: $fu-text;
  overflow-wrap: anywhere;
}

.tb-total {
  margin: 1px 0 0;
  font-size: 12.5px;
  color: $fu-text-muted;

  b { @include fu-tabular; color: $fu-text-strong; font-weight: $fu-fw-semibold; }
}

.tb-ghost {
  @include fu-button-reset;
  @include fu-focus-ring;
  flex: none;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: 1px solid $fu-border;
  background: $fu-surface;
  color: $fu-text-muted;
  display: grid;
  place-items: center;
  cursor: pointer;

  &:hover { color: $fu-text; }
}

.tb-stats { margin-top: 12px; }

.tb-legend {
  list-style: none;
  margin: 12px 0 0;
  padding: 0;
  display: flex;
  gap: 14px;
  font-size: 12.5px;
  color: $fu-text-strong;

  li { display: inline-flex; align-items: center; gap: 6px; }
}

.tb-key {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  display: inline-block;

  &--calls { background: #0a7cf5; }
  &--second { background: #8fc2fa; }
}

.tb-svg {
  display: block;
  width: 100%;
  height: auto;
  margin-top: 6px;
  overflow: visible;
}

.tb-grid { stroke: $fu-border; stroke-width: 1; &.is-axis { stroke: $fu-border-strong; } }
.tb-ytick { font-size: 11px; fill: $fu-text-muted; }
.tb-xlab { font-size: 12px; fill: $fu-text-strong; }
.tb-val { font-size: 12px; font-weight: 600; fill: $fu-text; }
.tb-val--second { fill: $fu-text-muted; }
.tb-hit { fill: transparent; }
.tb-group:hover .tb-hit { fill: rgba(10, 124, 245, 0.05); }

.tb-insight {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin: 10px 0 0;
  padding: 10px 12px;
  border-radius: 14px;
  background: #e7f2fe;
  color: $fu-text-strong;
  font-size: 13px;
  line-height: 1.4;

  svg { flex: none; margin-top: 2px; color: #0a7cf5; }
}

.tb-table {
  margin-top: 12px;
  overflow-x: auto;

  table { @include fu-tabular; width: 100%; border-collapse: collapse; font-size: 12.5px; }
  th, td { padding: 6px 8px; border-bottom: 1px solid $fu-border; text-align: right; }
  th:first-child, td:first-child { text-align: left; }
  thead th { color: $fu-text-muted; font-weight: $fu-fw-medium; }
  tbody th { font-weight: $fu-fw-regular; color: $fu-text-strong; }
}
</style>
